import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { Headline } from '@oneaudi/unified-web-components';
import { useSelector } from 'react-redux';
import {
  Container,
  HeaderContainer,
  ListWrapper,
  ModelFamilyContainer,
} from './VehicleOffersList.styles';
import VehicleTile from './VehicleTile/VehicleTile';
import { FilterOption } from '../../types';
import { PromotionType } from '../../types/offers';
import { Divider, ModelTitleWrapper } from '../OffersListContainer/OffersListContainer.style';
import { CopyOne } from '../Common/Common.styles';
import {
  ViewMoreDesktopWrapper,
  ViewMoreMobileWrapper,
  ViewMoreButton,
} from '../CashOffers/CashOffers.style';
import ArrowDown from '../../assets/icons/ArrowDown';
import { orderAndSortOffers, sortOffers } from '../../utils/orderAndSortOffers';
import VehicleTileCa from './VehicleTileCa';
import { selectZipCode } from '../../reducers/zipCodeReducer';

type VehicleOffersListProps = {
  modelFamilyOptions: {
    [key: string]: FilterOption;
  };
  modelFamilyKey: string;
  offersToRender: PromotionType[];
  isAnyFilterSelected: boolean;
  isFiltersOpen: boolean;
};

const VehicleOffersList: React.FC<VehicleOffersListProps> = ({
  modelFamilyOptions,
  offersToRender,
  modelFamilyKey,
  isAnyFilterSelected,
  isFiltersOpen,
}) => {
  const { t } = useTranslation();
  const [showViewMore, setShowViewMore] = useState(false);

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const toggleHiddenOffers = useCallback(() => {
    setShowViewMore(!showViewMore);
  }, [showViewMore]);

  useEffect(() => {
    if (isAnyFilterSelected) {
      setShowViewMore(true);
      return;
    }
    setShowViewMore(false);
  }, [isAnyFilterSelected]);

  // sorted offers by program priority and then by best offer in each type
  const sortedOffers = isCaMarket ? offersToRender : sortOffers(offersToRender);

  // Up to 6 offers showing initially on the page
  const offersOnInitialLoad = orderAndSortOffers(sortedOffers);

  // The rest of the offers, that we are hiding.
  const hiddenOffers = _.differenceWith(sortedOffers, offersOnInitialLoad, _.isEqual);

  const hasViewMoreButton = useMemo(() => {
    // If we have some hidden offers and we have more than one offer for this model family
    // we show "view more" button which will toggle showing hidden offers
    const hasSomeHiddenOffers = !!hiddenOffers.length;
    const hasOnlyOneOffer = sortedOffers.length === 1;

    // Checking if we don't have any regular offers - we are shoding all hidden offers and removing show more cta
    return hasSomeHiddenOffers && !hasOnlyOneOffer;
  }, [hiddenOffers.length, sortedOffers.length]);

  const listOfOffers = useMemo(() => {
    // If total number of offers 6 or less - showing all
    if (sortedOffers.length <= 6) return sortedOffers;

    // If we have hidden offers, and show more butoon wasn't clicked 0 we are showeing only 6 initial offers
    if (hasViewMoreButton && !showViewMore) return offersOnInitialLoad;

    // Combining initial and hidden offers
    return [...offersOnInitialLoad, ...hiddenOffers];
  }, [sortedOffers, hasViewMoreButton, showViewMore, offersOnInitialLoad, hiddenOffers]);

  const ViewMoreCTA = useMemo(() => {
    const initialOffersCount = offersOnInitialLoad.length;
    const totalCount = sortedOffers.length;
    const showingCount = showViewMore ? totalCount : initialOffersCount;
    return (
      <ViewMoreButton
        data-testid="view-more-offers"
        onClick={toggleHiddenOffers}
        shouldRotate={showViewMore}
      >
        <Trans
          i18nKey="promo.offers.viewOffers"
          values={{ showingCount, totalCount }}
          components={{
            bold: <span style={{ fontWeight: '700' }} />,
          }}
        />
        <ArrowDown />
      </ViewMoreButton>
    );
  }, [offersOnInitialLoad.length, showViewMore, sortedOffers.length, toggleHiddenOffers]);

  return (
    <ModelFamilyContainer key={modelFamilyKey}>
      <HeaderContainer isFiltersOpen={isFiltersOpen}>
        <ModelTitleWrapper>
          <Headline
            data-testid={`${modelFamilyKey}-modelFamily-title`}
            tag="h2"
            variant={{
              order: '2',
              type: 'headline',
              weight: 'bold',
            }}
          >
            {modelFamilyOptions[modelFamilyKey]?.label}
          </Headline>
          <CopyOne data-testid={`${modelFamilyKey}-modelFamily-count`}>
            {sortedOffers.length} {t('promo.offers.availableCount')}
          </CopyOne>
        </ModelTitleWrapper>
        {!!hasViewMoreButton && <ViewMoreDesktopWrapper>{ViewMoreCTA}</ViewMoreDesktopWrapper>}
      </HeaderContainer>
      <div>
        <Container>
          <ListWrapper data-testid={`${modelFamilyKey}-list-wrapper`}>
            {listOfOffers.map((offer, index) => {
              return isCaMarket ? (
                <VehicleTileCa
                  // change the key to id once id is available
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  offer={offer}
                />
              ) : (
                <VehicleTile
                  // change the key to id once id is available
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  offer={offer}
                  offerType={offer?.offerType?.toLowerCase() ?? ''}
                />
              );
            })}
          </ListWrapper>
        </Container>
      </div>
      {!!hasViewMoreButton && <ViewMoreMobileWrapper>{ViewMoreCTA}</ViewMoreMobileWrapper>}
      <Divider />
    </ModelFamilyContainer>
  );
};

export default VehicleOffersList;
